import { Injectable } from "@angular/core";
import { FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { AppConst } from "src/app/app-const";

// チェックボックス用：必須バリデータ
export function isAnyChecked(): ValidatorFn {
  return function validate(formGroup) {
    const parent = formGroup as FormGroup;
    if (parent.value.value !== undefined || parent.value.value === null) {
      // nameはvalueがなくてエラーになるので無視
      let checked = false;
      for (const check of parent.value.value) {
        if (check.value.selected) {
          checked = true;
          break;
        }
      }
      return checked ? null : { require: true };
    }
    return null;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ReservationCheckService {

  constructor(
  ) { }

  // バリデーション関連
  /**
   * 管理画面から設定されたバリデーション項目をもとにFormControl用のValidatorを作成する
   * @param type  項目タイプ
   * @param checks
   * @param rangeLower
   * @param rangeUpper
   * @param colLower
   * @param colUpper
   * @returns
   */
  public createValidatorList(type: string,
                             checks: string[],
                             rangeLower?: number | null,
                             rangeUpper?: number | null,
                             colLower?: number | null,
                             colUpper?: number | null): ValidatorFn[] {

    const validators: ValidatorFn[] = [];

    for (const check of checks) {
      if (check === AppConst.QUESTION_ITEM_CHECK_REQUIRED) {
        // 必須
        validators.push(Validators.required);

      } else if (check === AppConst.QUESTION_ITEM_CHECK_NUMBER) {
        // 数値
        const regex = /^[0-9]*$/;
        validators.push(Validators.pattern(regex));

      } else if (check === AppConst.QUESTION_ITEM_CHECK_HALF_ALPHANUMERIC) {
        // 半角英数
        const alphanumMulti   = '^[0-9a-zA-Z]([0-9a-zA-Z\x0a\x0d\x20])*?[0-9a-zA-Z]$';
        const alphanumSingle  = '^[0-9a-zA-Z]$';
        const regex = alphanumMulti + '|' + alphanumSingle;
        validators.push(Validators.pattern(regex));

      } else if (check === AppConst.QUESTION_ITEM_CHECK_FULL) {
        // 全角
        /**
         * 先頭: 1byte文字以外のみ
         * 中間: 1byte文字以外、全角スペースのみ
         * 終端: 1byte文字以外のみ
         * ※半角スペースは使用不可、1byteのうち英大文字は許可
         */
        const textMulti   = '^[^\x20-\x40\x5b-\x7E　]([^\x20-\x40\x5b-\x7E]|[　])*?[^\x20-\x40\x5b-\x7E　]$';
        const textSingle  = '^[^\x20-\x40\x5b-\x7E　]$';
        const regex = textMulti + '|' + textSingle;
        validators.push(Validators.pattern(regex));

      } else if (check === AppConst.QUESTION_ITEM_CHECK_FULL_KANA) {
        // 全角カナ
        /**
         * 先頭: 全角カナのみ
         * 中間: 全角カナ、全角スペース、改行のみ
         * 終端: 全角カナのみ
         */
        const textMulti   = '^[ァ-ンヴー]([ァ-ンヴー\x0a\x0d　])*?[ァ-ンヴー]$';
        const textSingle  = '^[ァ-ンヴー]$';
        const regex = textMulti + '|' + textSingle;
        validators.push(Validators.pattern(regex));

      } else if (check === AppConst.QUESTION_ITEM_CHECK_FULL_HIRA) {
        // 全角かな
        /**
         * 先頭: 全角かなのみ
         * 中間: 全角かな、全角スペース、改行のみ
         * 終端: 全角かなのみ
         */
        const textMulti   = '^[ぁ-んー]([ぁ-んー\x0a\x0d　])*?[ぁ-んー]$';
        const textSingle  = '^[ぁ-んー]$';
        const regex = textMulti + '|' + textSingle;
        validators.push(Validators.pattern(regex));

      } else if (check === AppConst.QUESTION_ITEM_CHECK_EMAIL) {
        // メールアドレス
        validators.push(Validators.email);

      } else if (check === AppConst.QUESTION_ITEM_CHECK_URL) {
        // URL
        const regex = /^(http|https)?:\/\//;
        validators.push(Validators.pattern(regex));

      } else if (check === AppConst.QUESTION_ITEM_CHECK_TEL) {
        // 電話番号（10,11桁）※stringのみ判定可能
        const telPattern1 = '0\\d-\\d{4}-\\d{4}';    // 固定電話(0x-xxxx-xxxx):市外局番1桁
        const telPattern2 = '0\\d{2}-\\d{3}-\\d{4}'; // 固定電話(0xx-xxx-xxxx):市外局番2桁
        const telPattern3 = '0\\d{3}-\\d{2}-\\d{4}'; // 固定電話(0xxx-xx-xxxx):市外局番3桁
        const telPattern4 = '0\\d{3}-\\d{3}-\\d{3}'; // 固定電話(0xxx-xxx-xxx):ナビ/フリーダイヤル
        const telPattern5 = '0\\d0-\\d{4}-\\d{4}';   // 携帯電話(0x0-xxxx-xxxx)
        const patternAll = '^(' + telPattern1 + '|' + telPattern2 + '|' + telPattern3 + '|' + telPattern4 + '|' + telPattern5 + ')$';

        const regex = new RegExp(patternAll);
        validators.push(Validators.pattern(regex));

      } else if (check === AppConst.QUESTION_ITEM_CHECK_RANGE) {
        // 範囲
        if (type == 'number') {
          // 数値だけ設定する
          if (!!rangeLower) { validators.push(Validators.min(rangeLower)) };
          if (!!rangeUpper) { validators.push(Validators.max(rangeUpper)) };
        }
        // NOTE: 日付/日時は値の入力時に設定する

      } else if (check === AppConst.QUESTION_ITEM_CHECK_COL_COUNT) {
        // 文字数
        if (!!colLower) { validators.push(Validators.minLength(colLower)) };
        if (!!colUpper) { validators.push(Validators.maxLength(colUpper)) };
      }
    }

    return validators;
  }

}
